import { useEffect, useRef, useState } from "react";
import Cache from "../utils/Cache";

// Matches the "min(Npx, 90vh)" convention useResizableDrawerWidth already
// uses for width — leaves room for the browser chrome above the element.
const VIEWPORT_MAX_RATIO = 0.9;
const DEFAULT_KEY_STEP = 24;

const clampHeight = (value, min, max) =>
  Math.round(Math.min(Math.max(value, min), max));

// Drag-to-resize state for an element resized from its BOTTOM edge — the
// vertical sibling of useResizableDrawerWidth (right-edge, width). Height can
// only move between `minHeight` (the floor, default `defaultHeight`) and
// `maxHeight` (further capped by 90% of the viewport). Persists per usage via
// `storageKey`, mirroring the same Cache-backed precedent.
const useResizableHeight = ({
  storageKey,
  defaultHeight,
  minHeight = defaultHeight,
  maxHeight = 1000,
  enabled = true,
  step = DEFAULT_KEY_STEP,
}) => {
  const cacheKey = `frame-height-${storageKey}`;

  const [height, setHeight] = useState(() => {
    const stored = parseInt(Cache.get(cacheKey), 10);
    const effectiveMax = Math.min(
      maxHeight,
      window.innerHeight * VIEWPORT_MAX_RATIO
    );
    return clampHeight(
      Number.isFinite(stored) ? stored : defaultHeight,
      minHeight,
      effectiveMax
    );
  });
  const [isDragging, setIsDragging] = useState(false);

  // Refs, not state: read synchronously inside pointer handlers without
  // waiting on a re-render, and without re-subscribing handlers every drag.
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(height);
  const latestHeightRef = useRef(height);
  const isDraggingRef = useRef(false);

  const handlePointerDown = (event) => {
    if (!enabled) return;
    event.preventDefault(); // no text-selection / native drag ghost
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartY.current = event.clientY;
    dragStartHeight.current = height;
    latestHeightRef.current = height;
    isDraggingRef.current = true;
    setIsDragging(true);
  };

  // The handle sits on the bottom edge. Moving the pointer down (clientY
  // increases) must grow the element, so delta is current-minus-start.
  const handlePointerMove = (event) => {
    if (!isDraggingRef.current) return; // guards stray hover pointermoves pre-drag
    const max = Math.min(maxHeight, window.innerHeight * VIEWPORT_MAX_RATIO);
    const deltaY = event.clientY - dragStartY.current;
    const next = clampHeight(dragStartHeight.current + deltaY, minHeight, max);
    latestHeightRef.current = next;
    setHeight(next);
  };

  const endDrag = (event) => {
    if (!isDraggingRef.current) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isDraggingRef.current = false;
    setIsDragging(false);
    // Persist once, at drag-end — not on every pointermove.
    Cache.set(cacheKey, String(latestHeightRef.current));
  };

  const handleKeyDown = (event) => {
    if (!enabled) return;
    const max = Math.min(maxHeight, window.innerHeight * VIEWPORT_MAX_RATIO);
    let next = null;
    if (event.key === "ArrowDown")
      next = clampHeight(height + step, minHeight, max);
    else if (event.key === "ArrowUp")
      next = clampHeight(height - step, minHeight, max);
    else if (event.key === "Home") next = minHeight;
    else if (event.key === "End") next = max;
    if (next === null) return;
    event.preventDefault();
    latestHeightRef.current = next;
    setHeight(next);
    Cache.set(cacheKey, String(next)); // each keypress is a discrete, complete action
  };

  // Pointer capture releases itself automatically when the capturing element
  // is removed from the DOM, so an unmount mid-drag needs no cleanup there.
  // The one thing that does need cleanup is this global body-style mutation.
  useEffect(() => {
    if (!isDragging) return undefined;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isDragging]);

  return {
    height,
    isDragging,
    handleProps: {
      enabled,
      height,
      minHeight,
      maxHeight: Math.min(maxHeight, window.innerHeight * VIEWPORT_MAX_RATIO),
      isDragging,
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown: handleKeyDown,
    },
  };
};

export default useResizableHeight;
